/**
 * Selectable "look" presets for the Realistic preview - each is a bundle of
 * CSS custom-property overrides (read by RealisticStage.css) rather than a
 * separate code path, so adding a new look is just adding an entry here.
 * "natural" reproduces the original single fixed look; everything else is
 * a deliberate exaggeration in one direction (older, brighter, dimmer,
 * cleaner) built from the same layers.
 */
export const EFFECT_STYLES = [
  {
    key: "natural",
    label: "Natural",
    vars: {
      "--dof-blur": "2.2px",
      "--card-sepia": "0.1",
      "--card-contrast": "0.97",
      "--card-brightness": "1",
      "--photo-sepia": "0.35",
      "--photo-contrast": "0.94",
      "--photo-brightness": "0.98",
      "--grain-opacity": "0.22",
      "--grain2-opacity": "0.13",
      "--photo-grain-opacity": "0.3",
      "--glare-opacity": "1",
      "--vignette-opacity": "0.4",
    },
  },
  {
    key: "vintage",
    label: "Vintage / old card",
    vars: {
      "--dof-blur": "2.8px",
      "--card-sepia": "0.45",
      "--card-contrast": "0.82",
      "--card-brightness": "0.93",
      "--photo-sepia": "0.7",
      "--photo-contrast": "0.78",
      "--photo-brightness": "0.90",
      "--grain-opacity": "0.48",
      "--grain2-opacity": "0.32",
      "--photo-grain-opacity": "0.62",
      "--glare-opacity": "0.55",
      "--vignette-opacity": "0.68",
    },
  },
  {
    key: "aged",
    label: "Aged / weathered",
    vars: {
      "--dof-blur": "2.4px",
      "--card-sepia": "0.55",
      "--card-contrast": "0.75",
      "--card-brightness": "0.88",
      "--photo-sepia": "0.8",
      "--photo-contrast": "0.72",
      "--photo-brightness": "0.85",
      "--grain-opacity": "0.56",
      "--grain2-opacity": "0.38",
      "--photo-grain-opacity": "0.72",
      "--glare-opacity": "0.4",
      "--vignette-opacity": "0.75",
    },
  },
  {
    key: "flash",
    label: "Camera flash",
    vars: {
      "--dof-blur": "1.4px",
      "--card-sepia": "0",
      "--card-contrast": "1.1",
      "--card-brightness": "1.08",
      "--photo-sepia": "0",
      "--photo-contrast": "1.06",
      "--photo-brightness": "1.08",
      "--grain-opacity": "0.14",
      "--grain2-opacity": "0.08",
      "--photo-grain-opacity": "0.16",
      "--glare-opacity": "1",
      "--vignette-opacity": "0.28",
    },
  },
  {
    key: "lowlight",
    label: "Low light",
    vars: {
      "--dof-blur": "3.2px",
      "--card-sepia": "0.16",
      "--card-contrast": "0.92",
      "--card-brightness": "0.86",
      "--photo-sepia": "0.24",
      "--photo-contrast": "0.88",
      "--photo-brightness": "0.82",
      "--grain-opacity": "0.4",
      "--grain2-opacity": "0.26",
      "--photo-grain-opacity": "0.44",
      "--glare-opacity": "0.5",
      "--vignette-opacity": "0.62",
    },
  },
  {
    key: "studio",
    label: "Studio clean",
    vars: {
      "--dof-blur": "1.6px",
      "--card-sepia": "0",
      "--card-contrast": "1.02",
      "--card-brightness": "1.02",
      "--photo-sepia": "0",
      "--photo-contrast": "1",
      "--photo-brightness": "1.01",
      "--grain-opacity": "0.06",
      "--grain2-opacity": "0.04",
      "--photo-grain-opacity": "0.08",
      "--glare-opacity": "0.6",
      "--vignette-opacity": "0.18",
    },
  },
  {
    key: "faded",
    label: "Sun faded",
    vars: {
      "--dof-blur": "2.6px",
      "--card-sepia": "0.35",
      "--card-contrast": "0.68",
      "--card-brightness": "1.15",
      "--photo-sepia": "0.5",
      "--photo-contrast": "0.65",
      "--photo-brightness": "1.12",
      "--grain-opacity": "0.38",
      "--grain2-opacity": "0.22",
      "--photo-grain-opacity": "0.42",
      "--glare-opacity": "0.35",
      "--vignette-opacity": "0.35",
    },
  },
  {
    key: "damaged",
    label: "Damaged/worn",
    vars: {
      "--dof-blur": "2.8px",
      "--card-sepia": "0.6",
      "--card-contrast": "0.7",
      "--card-brightness": "0.82",
      "--photo-sepia": "0.75",
      "--photo-contrast": "0.68",
      "--photo-brightness": "0.78",
      "--grain-opacity": "0.65",
      "--grain2-opacity": "0.45",
      "--photo-grain-opacity": "0.78",
      "--glare-opacity": "0.3",
      "--vignette-opacity": "0.82",
    },
  },
  {
    key: "water",
    label: "Water damaged",
    vars: {
      "--dof-blur": "3.4px",
      "--card-sepia": "0.4",
      "--card-contrast": "0.62",
      "--card-brightness": "0.78",
      "--photo-sepia": "0.6",
      "--photo-contrast": "0.58",
      "--photo-brightness": "0.75",
      "--grain-opacity": "0.72",
      "--grain2-opacity": "0.52",
      "--photo-grain-opacity": "0.85",
      "--glare-opacity": "0.2",
      "--vignette-opacity": "0.88",
    },
  },
  {
    key: "dusty",
    label: "Dusty/dirty",
    vars: {
      "--dof-blur": "2.9px",
      "--card-sepia": "0.3",
      "--card-contrast": "0.75",
      "--card-brightness": "0.85",
      "--photo-sepia": "0.45",
      "--photo-contrast": "0.72",
      "--photo-brightness": "0.82",
      "--grain-opacity": "0.62",
      "--grain2-opacity": "0.42",
      "--photo-grain-opacity": "0.68",
      "--glare-opacity": "0.25",
      "--vignette-opacity": "0.72",
    },
  },
  {
    key: "professional",
    label: "Professional scan",
    vars: {
      "--dof-blur": "1.2px",
      "--card-sepia": "0.02",
      "--card-contrast": "1.08",
      "--card-brightness": "1.04",
      "--photo-sepia": "0.05",
      "--photo-contrast": "1.05",
      "--photo-brightness": "1.03",
      "--grain-opacity": "0.04",
      "--grain2-opacity": "0.02",
      "--photo-grain-opacity": "0.05",
      "--glare-opacity": "0.4",
      "--vignette-opacity": "0.12",
    },
  },
  {
    key: "film",
    label: "Film camera",
    vars: {
      "--dof-blur": "2.5px",
      "--card-sepia": "0.25",
      "--card-contrast": "0.88",
      "--card-brightness": "0.96",
      "--photo-sepia": "0.5",
      "--photo-contrast": "0.85",
      "--photo-brightness": "0.93",
      "--grain-opacity": "0.52",
      "--grain2-opacity": "0.35",
      "--photo-grain-opacity": "0.58",
      "--glare-opacity": "0.65",
      "--vignette-opacity": "0.55",
    },
  },
  {
    key: "night",
    label: "Night mode",
    vars: {
      "--dof-blur": "3.6px",
      "--card-sepia": "0.2",
      "--card-contrast": "0.85",
      "--card-brightness": "0.72",
      "--photo-sepia": "0.3",
      "--photo-contrast": "0.82",
      "--photo-brightness": "0.68",
      "--grain-opacity": "0.68",
      "--grain2-opacity": "0.48",
      "--photo-grain-opacity": "0.75",
      "--glare-opacity": "0.15",
      "--vignette-opacity": "0.85",
    },
  },
  {
    key: "hires",
    label: "High resolution",
    vars: {
      "--dof-blur": "1.1px",
      "--card-sepia": "0.05",
      "--card-contrast": "1.12",
      "--card-brightness": "1.06",
      "--photo-sepia": "0.1",
      "--photo-contrast": "1.1",
      "--photo-brightness": "1.05",
      "--grain-opacity": "0.02",
      "--grain2-opacity": "0.01",
      "--photo-grain-opacity": "0.03",
      "--glare-opacity": "0.7",
      "--vignette-opacity": "0.08",
    },
  },
  {
    key: "xerox",
    label: "Xerox/copy",
    vars: {
      "--dof-blur": "2.2px",
      "--card-sepia": "0.15",
      "--card-contrast": "1.25",
      "--card-brightness": "0.95",
      "--photo-sepia": "0.2",
      "--photo-contrast": "1.22",
      "--photo-brightness": "0.92",
      "--grain-opacity": "0.48",
      "--grain2-opacity": "0.32",
      "--photo-grain-opacity": "0.52",
      "--glare-opacity": "0.35",
      "--vignette-opacity": "0.3",
    },
  },
  {
    key: "infrared",
    label: "Infrared effect",
    vars: {
      "--dof-blur": "2.4px",
      "--card-sepia": "0.8",
      "--card-contrast": "0.92",
      "--card-brightness": "1.1",
      "--photo-sepia": "0.95",
      "--photo-contrast": "0.9",
      "--photo-brightness": "1.08",
      "--grain-opacity": "0.32",
      "--grain2-opacity": "0.18",
      "--photo-grain-opacity": "0.38",
      "--glare-opacity": "0.8",
      "--vignette-opacity": "0.45",
    },
  },
];

export const DEFAULT_EFFECT = "natural";

export function getEffect(key) {
  return EFFECT_STYLES.find((e) => e.key === key) || EFFECT_STYLES[0];
}
